import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";

const LanguageContext = createContext(null);

export const useLanguage = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) {
    throw new Error("useLanguage must be used within LanguageProvider");
  }
  return ctx;
};

const DEFAULT_LANGUAGE = (
  process.env.GATSBY_MAIN_LANGUAGE || "PL"
).toUpperCase();

const ALLOWED_LANGUAGES = ["PL", "EN"];

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    // Initialize from localStorage on first render to avoid flashing wrong language
    if (typeof window === "undefined") return DEFAULT_LANGUAGE;
    const stored = localStorage.getItem("language");
    return stored && ALLOWED_LANGUAGES.includes(stored)
      ? stored
      : DEFAULT_LANGUAGE;
  });

  // Resolve language on client mount
  useEffect(() => {
    if (typeof window === "undefined") return;

    // 🔹 PRIORITY 1: localStorage (user's explicit selection - highest priority)
    const stored = localStorage.getItem("language");
    if (stored && ALLOWED_LANGUAGES.includes(stored)) {
      setLanguage(stored);
      return;
    }

    // 🔹 PRIORITY 2: Detect language from URL path (e.g., /pl/product -> PL)
    const urlLang = window.location.pathname
      .split("/")
      .filter(Boolean)[0]
      ?.toUpperCase();
    if (urlLang && ALLOWED_LANGUAGES.includes(urlLang)) {
      localStorage.setItem("language", urlLang);
      setLanguage(urlLang);
      return;
    }

    // 🔹 PRIORITY 3: Check cookie (from previous geolocation call)
    const cookieMatch = document.cookie.match(/(?:^|; )lang=([^;]+)/);
    if (cookieMatch?.[1]) {
      const lang = cookieMatch[1].toUpperCase();
      if (ALLOWED_LANGUAGES.includes(lang)) {
        localStorage.setItem("language", lang);
        setLanguage(lang);
        return;
      }
    }

    // 🔹 PRIORITY 4: Call geolocation on first visit
    fetch("/geolocation", { credentials: "same-origin" })
      .then(() => {
        // After geolocation call, check if cookie was set
        const match = document.cookie.match(/(?:^|; )lang=([^;]+)/);
        if (match?.[1]) {
          const lang = match[1].toUpperCase();
          if (ALLOWED_LANGUAGES.includes(lang)) {
            localStorage.setItem("language", lang);
            setLanguage(lang);
            return;
          }
        }
        // Fallback if cookie not set
        localStorage.setItem("language", DEFAULT_LANGUAGE);
        setLanguage(DEFAULT_LANGUAGE);
      })
      .catch(() => {
        // Network error - use default
        localStorage.setItem("language", DEFAULT_LANGUAGE);
        setLanguage(DEFAULT_LANGUAGE);
      });
  }, []);

  // Update <html lang> when language changes
  useEffect(() => {
    if (!language || typeof document === "undefined") return;
    document.documentElement.lang = language.toLowerCase();
  }, [language]);

  const switchLanguage = useCallback((langCode) => {
    if (!ALLOWED_LANGUAGES.includes(langCode)) return;
    setLanguage(langCode);
    localStorage.setItem("language", langCode);
  }, []);

  return (
    <LanguageContext.Provider value={{ language, switchLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
};
